import { useState, useEffect, useRef } from "react";
import { Phone, MessageCircle, MapPin, Clock, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/lib/i18n";

// PassCar Real Contact Info
const WHATSAPP_NUMBER = "351939507334";
const WHATSAPP_MESSAGE = encodeURIComponent("Olá Igor! ");
const PHONE_NUMBER = "+351 939 507 334";
const EMAIL = "admin@passcar.pt";
const ADDRESS = "Estrada Nacional 249 4, Abóboda";
const ADDRESS_CITY = "São Domingos de Rana – Oeiras, Lisboa";
const POSTAL_CODE = "2785-035 ";

export function ContactSection() {
  const { t } = useI18n();
  const [showMap, setShowMap] = useState(false);
  const mapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShowMap(true);
          observer.disconnect();
        }
      },
      { rootMargin: "800px" },
    );
    if (mapRef.current) observer.observe(mapRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="contacto" className="py-20">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">{t.contact.title}</h2>
            <p className="text-muted-foreground text-lg">{t.contact.subtitle}</p>
          </div>

          {/* Contact Cards */}
          <div className="grid md:grid-cols-2 gap-6">
            {/* Quick Contact */}
            <div className="glass-card p-8 text-center space-y-6">
              <h3 className="text-xl font-semibold">Contacto Rápido</h3>

              <div className="flex flex-col gap-4">
                <a
                  href={`https://wa.me/${WHATSAPP_NUMBER}?text=${WHATSAPP_MESSAGE}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full"
                >
                  <Button size="lg" className="w-full btn-glow gap-2 text-lg py-6">
                    <MessageCircle className="h-5 w-5" />
                    {t.contact.whatsapp}
                  </Button>
                </a>

                <a href={`tel:${PHONE_NUMBER.replace(/\s/g, "")}`} className="w-full">
                  <Button variant="outline" size="lg" className="w-full gap-2 text-lg py-6">
                    <Phone className="h-5 w-5" />
                    {t.contact.call}
                  </Button>
                </a>

                <a href={`mailto:${EMAIL}`} className="w-full">
                  <Button variant="outline" size="lg" className="w-full gap-2 text-lg py-6">
                    <Mail className="h-5 w-5" />
                    Email
                  </Button>
                </a>
              </div>

              <p className="text-muted-foreground text-sm">
                {PHONE_NUMBER} • {EMAIL}
              </p>
            </div>

            {/* Location & Hours */}
            <div className="glass-card p-8 space-y-6">
              <div className="flex items-start gap-4">
                <div className="p-3 rounded-2xl bg-primary/10">
                  <MapPin className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <h4 className="font-semibold mb-1">{t.contact.location}</h4>
                  <p className="text-muted-foreground text-sm">
                    {ADDRESS}
                    <br />
                    {ADDRESS_CITY}
                    <br />
                    {POSTAL_CODE}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="p-3 rounded-2xl bg-primary/10">
                  <Clock className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <h4 className="font-semibold mb-1">{t.contact.hours}</h4>
                  <p className="text-muted-foreground text-sm">
                    Segunda a Sábado: 9h - 18h
                    <br />
                    Domingo: Fechado
                  </p>
                </div>
              </div>

              {/* Google Maps Embed - lazy loaded */}
              <div ref={mapRef} className="aspect-video rounded-2xl overflow-hidden bg-secondary">
                {showMap ? (
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d4302.482354476128!2d-9.337569418708329!3d38.72858272217853!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0xd1ecf06cbed88e1%3A0xae0013d4ac42b696!2sPasscar!5e1!3m2!1spt-PT!2spt!4v1770231004186!5m2!1spt-PT!2spt"
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="Localização PassCar"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-muted-foreground text-sm">
                    <MapPin className="h-5 w-5 mr-2 animate-pulse" /> A carregar mapa...
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
